// src/services/reportsService.js
import { reportsAPI } from './api';

export const reportsService = {
  // Get companies for dropdown
  async getCompanies() {
    try {
      console.log('🔄 Fetching companies for reports');
      const response = await reportsAPI.getCompanies();
      console.log('✅ Companies loaded:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching companies:', error);
      throw new Error('Failed to load companies: ' + error.message);
    }
  },

  // Get filter options (surveys, branches, departments, divisions) for a company
  async getFilterOptions(companyId) {
    try {
      console.log('🔄 Fetching filter options for company:', companyId);
      const response = await reportsAPI.getFilters(companyId);
      console.log('✅ Filter options loaded:', {
        surveys: response.data.surveys?.length || 0,
        branches: response.data.branches?.length || 0,
        departments: response.data.departments?.length || 0,
        divisions: response.data.divisions?.length || 0
      });
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching filter options:', error);
      throw new Error('Failed to load filter options: ' + error.message);
    }
  },

  // Generate comprehensive reports (branch, department, division)
  async generateReport(request) {
    try {
      console.log('🔄 Generating comprehensive reports:', {
        companyId: request.companyId,
        surveyCount: request.surveyIds?.length || 0,
        branchCount: request.branchIds?.length || 0,
        departmentCount: request.departmentIds?.length || 0,
        divisionCount: request.divisionIds?.length || 0,
        dateRange: {
          from: request.fromDate,
          to: request.toDate
        },
        includeBranch: request.IncludeBranchReport,
        includeDepartment: request.IncludeDepartmentReport,
        includeDivision: request.IncludeDivisionReport
      });

      const response = await reportsAPI.generateReport(request);
      
      console.log('✅ Reports generated successfully:', {
        categories: response.data.categories?.length || 0,
        branchReports: response.data.branchReports?.length || 0,
        departmentReports: response.data.departmentReports?.length || 0,
        divisionReports: response.data.divisionReports?.length || 0
      });
      
      return response.data;
    } catch (error) {
      console.error('❌ Error generating reports:', error);
      throw new Error('Failed to generate reports: ' + error.message);
    }
  },

  // Generate branch-specific report
  async generateBranchReport(request) {
    try {
      console.log('🔄 Generating branch report:', {
        companyId: request.companyId,
        surveyIds: request.surveyIds,
        branchIds: request.branchIds,
        dateRange: { from: request.fromDate, to: request.toDate }
      });

      const branchRequest = {
        ...request,
        IncludeBranchReport: true,
        IncludeDepartmentReport: false,
        IncludeDivisionReport: false
      };

      const response = await reportsAPI.generateReport(branchRequest);
      console.log('✅ Branch report generated:', response.data.branchReports?.length || 0, 'records');
      return response.data;
    } catch (error) {
      console.error('❌ Error generating branch report:', error);
      throw new Error('Failed to generate branch report: ' + error.message);
    }
  },

  // Generate department-specific report
  async generateDepartmentReport(request) {
    try {
      console.log('🔄 Generating department report:', {
        companyId: request.companyId,
        surveyIds: request.surveyIds,
        departmentIds: request.departmentIds,
        dateRange: { from: request.fromDate, to: request.toDate }
      });

      const deptRequest = {
        ...request,
        IncludeBranchReport: false,
        IncludeDepartmentReport: true,
        IncludeDivisionReport: false
      };

      const response = await reportsAPI.generateReport(deptRequest);
      console.log('✅ Department report generated:', response.data.departmentReports?.length || 0, 'records');
      return response.data;
    } catch (error) {
      console.error('❌ Error generating department report:', error);
      throw new Error('Failed to generate department report: ' + error.message);
    }
  },

  // Generate division-specific report
  async generateDivisionReport(request) {
    try {
      console.log('🔄 Generating division report:', {
        companyId: request.companyId,
        surveyIds: request.surveyIds,
        divisionIds: request.divisionIds,
        dateRange: { from: request.fromDate, to: request.toDate }
      });

      const divisionRequest = {
        ...request,
        IncludeBranchReport: false,
        IncludeDepartmentReport: false,
        IncludeDivisionReport: true
      };

      const response = await reportsAPI.generateReport(divisionRequest);
      console.log('✅ Division report generated:', response.data.divisionReports?.length || 0, 'records');
      return response.data;
    } catch (error) {
      console.error('❌ Error generating division report:', error);
      throw new Error('Failed to generate division report: ' + error.message);
    }
  },

  // Get report statistics and summary
  async getReportSummary(companyId, surveyIds = []) {
    try {
      console.log('🔄 Fetching report summary:', { companyId, surveyCount: surveyIds.length });
      
      // This could be a separate API endpoint or use the existing one with minimal data
      const request = {
        companyId,
        surveyIds,
        IncludeBranchReport: true,
        IncludeDepartmentReport: true,
        IncludeDivisionReport: true
      };

      const response = await reportsAPI.generateReport(request);
      
      const summary = {
        totalCategories: response.data.categories?.length || 0,
        totalBranchReports: response.data.branchReports?.length || 0,
        totalDepartmentReports: response.data.departmentReports?.length || 0,
        totalDivisionReports: response.data.divisionReports?.length || 0,
        categories: response.data.categories || []
      };

      console.log('✅ Report summary loaded:', summary);
      return summary;
    } catch (error) {
      console.error('❌ Error fetching report summary:', error);
      throw new Error('Failed to load report summary: ' + error.message);
    }
  },

  // Export reports to various formats
  async exportReport(request, format = 'pdf', reportType = 'all') {
    try {
      console.log('🔄 Exporting report:', {
        format,
        reportType,
        companyId: request.companyId,
        surveyCount: request.surveyIds?.length || 0
      });

      // Generate the report data first
      const reportData = await this.generateReport(request);
      
      // Prepare export data based on report type
      let exportData = {};
      
      switch (reportType.toLowerCase()) {
        case 'branch':
          exportData = {
            type: 'branch',
            data: reportData.branchReports,
            categories: reportData.categories
          };
          break;
        case 'department':
          exportData = {
            type: 'department',
            data: reportData.departmentReports,
            categories: reportData.categories
          };
          break;
        case 'division':
          exportData = {
            type: 'division',
            data: reportData.divisionReports,
            categories: reportData.categories
          };
          break;
        default:
          exportData = {
            type: 'comprehensive',
            branchReports: reportData.branchReports,
            departmentReports: reportData.departmentReports,
            divisionReports: reportData.divisionReports,
            categories: reportData.categories
          };
      }

      // Here you would typically send this to a backend endpoint that handles PDF/Excel generation
      // For now, we'll return a success response
      const exportResult = {
        success: true,
        format,
        reportType,
        data: exportData,
        timestamp: new Date().toISOString(),
        downloadUrl: null // This would be set by the backend
      };

      console.log('✅ Report export prepared:', {
        format,
        reportType,
        recordCount: exportData.data?.length || 0
      });

      return exportResult;
    } catch (error) {
      console.error('❌ Error exporting report:', error);
      throw new Error('Failed to export report: ' + error.message);
    }
  },

  // Quick report - generate with minimal parameters
  async generateQuickReport(companyId, surveyId) {
    try {
      console.log('🔄 Generating quick report:', { companyId, surveyId });
      
      const request = {
        companyId,
        surveyIds: [surveyId],
        branchIds: [],
        departmentIds: [],
        divisionIds: [],
        fromDate: null,
        toDate: null,
        IncludeBranchReport: true,
        IncludeDepartmentReport: true,
        IncludeDivisionReport: true
      };

      const response = await reportsAPI.generateReport(request);
      
      console.log('✅ Quick report generated:', {
        categories: response.data.categories?.length || 0,
        totalRecords: (response.data.branchReports?.length || 0) + 
                     (response.data.departmentReports?.length || 0) + 
                     (response.data.divisionReports?.length || 0)
      });
      
      return response.data;
    } catch (error) {
      console.error('❌ Error generating quick report:', error);
      throw new Error('Failed to generate quick report: ' + error.message);
    }
  },

  // Validate report request before sending
  validateReportRequest(request) {
    const errors = [];

    if (!request.companyId) {
      errors.push('Company ID is required');
    }

    if (!request.surveyIds || request.surveyIds.length === 0) {
      errors.push('At least one survey must be selected');
    }

    if (!request.IncludeBranchReport && !request.IncludeDepartmentReport && !request.IncludeDivisionReport) {
      errors.push('At least one report type (Branch, Department, or Division) must be selected');
    }

    if (request.fromDate && request.toDate && new Date(request.fromDate) > new Date(request.toDate)) {
      errors.push('From date cannot be after To date');
    }

    const isValid = errors.length === 0;
    
    console.log('🔍 Report request validation:', {
      isValid,
      errors: isValid ? 'None' : errors
    });

    return {
      isValid,
      errors
    };
  }
};

export default reportsService;